import React, { useState, createContext, useContext } from "react";

export const MODAL_TYPES = {
    LOGIN_MODAL: "LOGIN_MODAL",
    PROFILE_MODAL: "PROFILE_MODAL",
    SEARCH_EVENT_MODAL: "SEARCH_EVENT_MODAL",
};

// LoginModal is intentionally excluded from MODAL_COMPONENTS to avoid a circular
// dependency (GlobalModal ↔ LoginModal). The support app renders LoginModal directly
// from Header.tsx and RequireAuth.tsx — it is never triggered via showModal().
const MODAL_COMPONENTS: Record<string, React.ComponentType<any>> = {};

interface Store {
    modalType: string | null;
    modalProps?: any;
}

interface ModalContext {
    showModal: (modalType: string, modalProps?: any) => void;
    hideModal: () => void;
    store: Store | undefined;
}

type ContextProps = {
    children: React.ReactNode;
};

const GlobalModalContext = createContext<ModalContext | null>(null);

export const GlobalModal = ({ children }: ContextProps) => {
    const [store, setStore] = useState<Store>();
    const { modalType, modalProps } = store || {};

    const showModal = (modalType: string, modalProps: any = {}) => {
        setStore({ ...store, modalType, modalProps });
    };

    const hideModal = () => {
        setStore({ ...store, modalType: null, modalProps: {} });
    };

    const renderComponent = () => {
        if (!modalType) return null;
        const ModalComponent = MODAL_COMPONENTS[modalType];
        if (!ModalComponent) return null;
        return <ModalComponent id="global-modal" {...modalProps} />;
    };

    return (
        <GlobalModalContext.Provider value={{ store, showModal, hideModal }}>
            {renderComponent()}
            {children}
        </GlobalModalContext.Provider>
    );
};

export const useGlobalModalContext = () => {
    const context = useContext(GlobalModalContext);
    if (!context) {
        throw new Error("useGlobalModalContext must be used within a GlobalModal provider");
    }
    return context;
};
